import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, KeyRound, ArrowLeft, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '@/lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_inventory-engine-2/artifacts/2d9zezs2_Zippy%20Logo%202.PNG';

export default function ResetPassword({ onLogin }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) setErrorMsg('This link is missing a reset token.');
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (pw.length < 8) {
      setErrorMsg('Password must be at least 8 characters');
      return;
    }
    if (pw !== confirm) {
      setErrorMsg('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, {
        token, new_password: pw,
      });
      toast.success(`Password updated. Welcome back, ${res.data.user.name}.`);
      onLogin(res.data.token, res.data.user);
      navigate('/', { replace: true });
    } catch (err) {
      setErrorMsg(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-6">
          <img src={LOGO_URL} alt="Zippy WMS" className="h-14 w-auto" />
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <KeyRound className="h-6 w-6 text-[#1680e5]" />Choose a new password
            </CardTitle>
            <CardDescription>Minimum 8 characters. Pick something memorable but strong.</CardDescription>
          </CardHeader>
          <CardContent>
            {!token ? (
              <div className="flex items-start gap-3 rounded-lg bg-red-50 border border-red-200 p-4 mb-3">
                <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-800">
                  <p className="font-medium">Invalid reset link</p>
                  <p className="text-slate-600 mt-1">
                    Ask for a new one from the forgot-password page.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-pw">New password</Label>
                  <Input
                    id="reset-pw"
                    type="password"
                    required
                    minLength={8}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    autoFocus
                    data-testid="reset-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-confirm">Confirm new password</Label>
                  <Input
                    id="reset-confirm"
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-confirm-input"
                  />
                </div>
                {errorMsg && (
                  <div
                    className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                    data-testid="reset-error"
                  >
                    {errorMsg}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1680e5] hover:bg-[#1680e5]/90"
                  data-testid="reset-submit-button"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Updating…</>
                  ) : (
                    'Set new password & sign in'
                  )}
                </Button>
              </form>
            )}
            <Button asChild variant="ghost" className="w-full mt-3" size="sm">
              <Link to="/login">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to sign in
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
